import { useMutation } from '@tanstack/react-query';
import type { GraphQLClient, Variables } from 'graphql-request';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { UseMutationOptions } from '@tanstack/react-query';

import {
  defaultClient,
  fileUploadClient,
  PERSISTED_HASH_HEADER,
} from '@e-socna/graphql-clients';

import {
  PersistedQueryNotFoundError,
  sendPersistedQuery,
} from '../utils/send-persisted-query.util';
import { checkIfIsTransactionConflictError } from '../utils/check-if-is-transaction-conflict-error.util';
import { environment } from '../environment';

const generateUseGraphQLMutation = (
  client: Readonly<GraphQLClient>,
  useHeaderForPersistedQuery = false,
) =>
  function useGraphQLMutation<
    TResult,
    TVariables extends Variables | undefined,
    TError = Error,
  >(
    document: TypedDocumentNode<TResult, TVariables>,
    options?: UseMutationOptions<TResult, TError, TVariables>,
  ) {
    return useMutation({
      retry: (failureCount, error) =>
        failureCount < environment.transactionConflictMaxRetry &&
        checkIfIsTransactionConflictError(error),
      mutationFn: async (...[variables]: readonly [TVariables]) => {
        const hash = environment.enablePersistedQueries
          ? (document as { __meta__?: { hash?: string } }).__meta__?.hash
          : undefined;

        if (hash) {
          if (useHeaderForPersistedQuery) {
            // File upload client: embed hash in the multipart operations body
            // via the request middleware - cannot use sendPersistedQuery here
            // because variables may contain non-serialisable File objects.
            return client.request({
              document,
              variables,
              requestHeaders: { [PERSISTED_HASH_HEADER]: hash },
            });
          }

          try {
            return await sendPersistedQuery<TResult, TVariables>(
              hash,
              variables,
              {
                endpoint: `${environment.backendUrl}${environment.graphQLEndpoint}`,
              },
            );
          } catch (error) {
            if (!(error instanceof PersistedQueryNotFoundError)) {
              throw error;
            }
          }
        }

        return client.request({ document, variables });
      },
      ...options,
    });
  };

export const useGraphQLMutation = generateUseGraphQLMutation(defaultClient);

export const useGraphQLMutationForFileUpload = generateUseGraphQLMutation(
  fileUploadClient,
  true,
);
